const DASHBOARD_BASE_URL =
  (import.meta.env.VITE_DASHBOARD_URL as string) ??
  "https://dashboard.rockethealth.app";

export const MENS_HEALTH_TCONSULT_STANDARD_ID =
  "20edfa48-12e6-4cf0-9b60-7bd936820881";
export const MENS_HEALTH_CONSULT_STANDARD_ID =
  "83a6b867-e983-4229-b4ff-62ddfa287c60";
export const MENS_HEALTH_PAID_FOLLOW_UP_STANDARD_ID =
  "3d2e2325-147c-4cb4-aed0-d4a13965cb87";

export const MENS_HEALTH_BUNDLE_IDS = [
  MENS_HEALTH_TCONSULT_STANDARD_ID,
  MENS_HEALTH_PAID_FOLLOW_UP_STANDARD_ID,
  MENS_HEALTH_CONSULT_STANDARD_ID,
];

type MensHealthSession = {
  name?: string | null;
  start_time?: string | Date | null;
  status?: string | null;
  is_unsuccessful?: boolean | null;
};

const isActiveSession = (status?: string | null) =>
  !status || status.toLowerCase() === "active";

// A session counts toward scheduling decisions only when it is active (not
// cancelled) and was not marked unsuccessful. Marking a session unsuccessful
// releases its bundle slot server-side and treats it as if it never happened,
// so the scheduler must ignore it too — otherwise an unsuccessful first consult
// keeps routing the primary link to a follow-up, leaving no way to re-book the
// consult.
const isCountableSession = (session: MensHealthSession) =>
  isActiveSession(session.status) && !session.is_unsuccessful;

const isIndiaCountry = (country?: string | null) =>
  !country || country.toLowerCase() === "india";

const buildLink = (path: string, usageId: string, includeUsageId = true) =>
  `${DASHBOARD_BASE_URL}${path}${includeUsageId ? `?usage_id=${usageId}` : ""}`;

export const isMensHealthBundle = (standardId: string) =>
  MENS_HEALTH_BUNDLE_IDS.includes(standardId);

export const isMensHealthTconsultBundle = (standardId: string) =>
  standardId === MENS_HEALTH_TCONSULT_STANDARD_ID;

const getActiveConsultSessions = (sessions: MensHealthSession[] = []) =>
  sessions.filter(
    (session) =>
      isCountableSession(session) &&
      session.name?.toLowerCase().includes("consult"),
  );

const getActiveTherapySessions = (sessions: MensHealthSession[] = []) =>
  sessions.filter(
    (session) =>
      isCountableSession(session) &&
      session.name?.toLowerCase().includes("therapy"),
  );

const getFirstConsultTime = (consultSessions: MensHealthSession[] = []) => {
  const times = consultSessions
    .map((session) => new Date(session.start_time ?? "").getTime())
    .filter((time) => Number.isFinite(time));

  if (!times.length) return null;
  return Math.min(...times);
};

// Earliest start time across all countable (active, not-unsuccessful) sessions
// on the bundle.
const getFirstActiveSessionTime = (sessions: MensHealthSession[] = []) => {
  const times = sessions
    .filter(isCountableSession)
    .map((session) => new Date(session.start_time ?? "").getTime())
    .filter((time) => Number.isFinite(time));

  if (!times.length) return null;
  return Math.min(...times);
};

// Classifies a follow-up bundle's booking:
//  - 'none' → no active session on the bundle yet (show a generic scheduling link)
//  - 'free' → within 30 days of the first active session AND sessions remaining
//  - 'paid' → otherwise
const resolveFollowUpKind = (
  sessions: MensHealthSession[] = [],
  remainingSessions = 0,
): 'none' | 'free' | 'paid' => {
  const firstSessionTime = getFirstActiveSessionTime(sessions);
  if (firstSessionTime === null) return 'none';
  const isWithinThirtyDays =
    Date.now() <= firstSessionTime + 30 * 24 * 60 * 60 * 1000;
  return isWithinThirtyDays && remainingSessions > 0 ? 'free' : 'paid';
};

type MensHealthPrimaryArgs = {
  standard_id: string;
  sessions?: MensHealthSession[];
  remaining_sessions?: number;
  user_country?: string | null;
};

type MensHealthPrimaryResolution = {
  path: string;
  includeUsageId: boolean;
  /** Human-readable description of the link being copied. */
  label: string;
};

/**
 * Single source of truth for the men's-health "master" link: decides which
 * scheduler to point at and the label that describes it. Both the link and the
 * label getters below derive from this so they can never drift apart.
 */
function resolveMensHealthPrimary({
  standard_id,
  sessions,
  remaining_sessions,
  user_country,
}: MensHealthPrimaryArgs): MensHealthPrimaryResolution {
  const isTconsultBundle = isMensHealthTconsultBundle(standard_id);
  const isIndia = isIndiaCountry(user_country ?? undefined);
  const consultSessions = getActiveConsultSessions(sessions);
  const therapySessions = getActiveTherapySessions(sessions);

  const consultPath = isTconsultBundle
    ? "/schedule/tconsult"
    : "/schedule/consult";
  const consultPathWithCountry = isIndia
    ? consultPath
    : `${consultPath}-international`;

  const followUpRoutes = isIndia
    ? { free: "/schedule/free-followup", paid: "/schedule/follow-up" }
    : {
        free: "/schedule/free-followup-int",
        paid: "/schedule/follow-up-paid-int",
      };

  // Follow-up bundle: free within 30 days of the first session on the bundle
  // (while sessions remain), otherwise paid. With no active session yet we can't
  // apply the 30-day rule, so surface a generic "Scheduling" link. usage_id is
  // included either way so the booking is attributed to this bundle usage.
  if (standard_id === MENS_HEALTH_PAID_FOLLOW_UP_STANDARD_ID) {
    const kind = resolveFollowUpKind(sessions, remaining_sessions ?? 0);
    if (kind === 'none') {
      return { path: followUpRoutes.paid, includeUsageId: true, label: 'Scheduling' };
    }
    const isFreeFollowUp = kind === 'free';
    return {
      path: isFreeFollowUp ? followUpRoutes.free : followUpRoutes.paid,
      includeUsageId: true,
      label: isFreeFollowUp ? 'Free Follow Up' : 'Paid Follow Up',
    };
  }

  const hasSessions = (sessions ?? []).length > 0;
  if (!hasSessions) {
    return { path: consultPathWithCountry, includeUsageId: true, label: "Consultation" };
  }

  if (consultSessions.length === 0) {
    return { path: consultPathWithCountry, includeUsageId: true, label: "Consultation" };
  }

  if (
    isTconsultBundle &&
    consultSessions.length > 0 &&
    therapySessions.length === 0
  ) {
    return { path: "/schedule/menstherapy", includeUsageId: true, label: "Therapy" };
  }

  const firstConsultTime = getFirstConsultTime(consultSessions);
  if (firstConsultTime === null) {
    return { path: consultPathWithCountry, includeUsageId: true, label: "Consultation" };
  }

  const isWithinThirtyDays =
    Date.now() <= firstConsultTime + 30 * 24 * 60 * 60 * 1000;
  const remaining = remaining_sessions ?? 0;
  const isFreeFollowUp = isWithinThirtyDays && remaining > 0;

  const followUpPath = isFreeFollowUp
    ? followUpRoutes.free
    : followUpRoutes.paid;
  return {
    path: followUpPath,
    includeUsageId: isFreeFollowUp,
    label: isFreeFollowUp ? "Free Follow Up" : "Paid Follow Up",
  };
}

export function getMensHealthPrimaryLink({
  standard_id,
  usage_id,
  sessions,
  remaining_sessions,
  user_country,
}: {
  standard_id: string;
  usage_id: string;
  sessions?: MensHealthSession[];
  remaining_sessions?: number;
  user_country?: string | null;
}) {
  const { path, includeUsageId } = resolveMensHealthPrimary({
    standard_id,
    sessions,
    remaining_sessions,
    user_country,
  });
  return buildLink(path, usage_id, includeUsageId);
}

/** Label describing the link `getMensHealthPrimaryLink` would produce for the same args. */
export function getMensHealthPrimaryLabel(args: MensHealthPrimaryArgs) {
  return resolveMensHealthPrimary(args).label;
}

export function getMensHealthFollowUpLinks({
  usage_id,
  user_country,
}: {
  usage_id: string;
  user_country?: string | null;
}) {
  const isIndia = isIndiaCountry(user_country ?? undefined);
  const freePath = isIndia
    ? "/schedule/free-followup"
    : "/schedule/free-followup-int";
  const paidPath = isIndia
    ? "/schedule/follow-up"
    : "/schedule/follow-up-paid-int";

  return {
    free: buildLink(freePath, usage_id),
    paid: buildLink(paidPath, usage_id, false),
  };
}

export function getMensHealthTherapyLink({
  standard_id,
  usage_id,
  sessions,
}: {
  standard_id: string;
  usage_id: string;
  sessions?: MensHealthSession[];
}) {
  if (!isMensHealthTconsultBundle(standard_id)) return "";
  const therapySessions = getActiveTherapySessions(sessions);
  if (therapySessions.length > 0) return "";
  return buildLink("/schedule/menstherapy", usage_id);
}
